import { useEffect, useRef, type RefObject } from 'react';

interface UseInfiniteScrollSentinelOptions {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  /** How far below the viewport the sentinel triggers a load. Defaults to the ticket list's own value. */
  rootMargin?: string;
  /**
   * The scrollable ancestor to observe the sentinel against. Omit it for a list that scrolls
   * with the browser window (the default — root `null` means the viewport); pass the ref of
   * the actual scrolling container for a list inside one. Without this, on a page whose window
   * never scrolls, `rootMargin`'s look-ahead has no room past the container's own clipped edge,
   * so the sentinel only fires once it is already on screen instead of just before.
   */
  rootRef?: RefObject<Element | null>;
}

/**
 * Observes a sentinel element and asks for the next page once it scrolls into
 * view, instead of a scroll-event listener that would fire on every pixel.
 * Mirrors the IntersectionObserver pattern already used by the ticket list
 * and conversation list sentinels, pulled out so the gap board doesn't grow a
 * third inline copy of it.
 */
export function useInfiniteScrollSentinel({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  rootMargin = '120px',
  rootRef,
}: UseInfiniteScrollSentinelOptions) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      { root: rootRef?.current ?? null, rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore, rootMargin, rootRef]);

  return sentinelRef;
}
